import { GridFilterItem } from '@mui/x-data-grid-pro';

import { FilterState } from '../../components/molecules/FilterBar';
import { ColumnDef, applyColumnOrder } from '../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import {
  SERVICE_ADDRESS_SPLIT_COLUMNS,
  SERVICE_ADDRESS_SPLIT_TOGGLE_FIELD,
  columnSelectionItems,
} from '../../constants/service.constant';
import { buildAdvancedFilterItems, sanitizeSavedReportFilterItems } from '../../utils/filterModel';
import { getServiceStatusNamesByStatusGroup } from '../../utils/services';

// Re-exports from extracted modules so existing import paths keep working.
export type { FilterStateConversionLookups } from './serviceListFilterConversion';
export { filterModelToFilterState } from './serviceListFilterConversion';
export type { LabelValue } from './serviceListFilterOptions';
export {
  buildStatusOptions,
  buildStatusGroups,
  buildJobTypeOptions,
  buildClientOptions,
  buildContractOptions,
} from './serviceListFilterOptions';

/** Empty filter state — shared default for ServiceList and useReports. */
export const EMPTY_FILTERS: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: [],
  contractNames: [],
  extraText: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
};

/**
 * Factory-default column layout for the service list page — order + visibility.
 * Applied when the user has no saved default report. The listed fields render
 * left-to-right in the table; any columns not in this list remain hidden in
 * their original `columnSelectionItems` order and can be enabled via the
 * column picker.
 */
export const DEFAULT_VISIBLE_FIELDS = [
  'serviceId',
  'reference1',
  'logoPic',
  'priorityCode',
  'actionTypeName',
  'serviceStatusName',
  'primaryAllocation',
  'locationName',
  'clientSLASummary',
  'opsSLASummary',
];

/**
 * Pinned column layout for client/customer users. The "client view" of the
 * services list is curated — Save and Columns buttons are hidden and any
 * saved-report column overrides are ignored — so these columns are always
 * what a client sees in the order listed.
 */
export const CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS = [
  'serviceId',
  'logoPic',
  'reference1',
  'serviceTypeName',
  'locationName',
  'reportedFault',
  'serviceStatusName',
  'createdAt',
];

/**
 * Fixed widths (in px) for columns whose content has a known natural bound —
 * logos, short badges, etc. Sized to the *content*, not the header label, so
 * long header titles will ellipsize. Columns not listed here flex to fit
 * content.
 */
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  logoPic: 60,
  priorityCode: 80,
};

/**
 * Soft max widths (in px) for columns whose content varies but can sometimes
 * be arbitrarily long — notes, descriptions, free-text fields. The column
 * still shrinks when content is short; only long values are truncated with
 * an ellipsis at the cap.
 */
const DEFAULT_COLUMN_MAX_WIDTHS: Record<string, number> = {
  creationNotes: 180,
  lastUpdatedNote: 180,
  // "Parts Pending" renders the aggregated `pendedParts` list, which can grow
  // long when several parts are pending — cap it so it truncates with a tooltip.
  partsPending: 200,
};

/**
 * Soft min widths (in px) for columns that must stay legible even on narrow
 * viewports. The column can grow past this with content but will never
 * shrink below it in the browser's auto layout distribution.
 */
const DEFAULT_COLUMN_MIN_WIDTHS: Record<string, number> = {
  locationName: 220,
};

/**
 * Short labels to display in the visible table header. The full column name
 * (from columnSelectionItems) is still used by the column picker, exports,
 * tooltips, and accessibility labels. Only the on-screen header is shortened.
 */
const DEFAULT_COLUMN_HEADER_LABELS: Record<string, string> = {
  logoPic: 'C',
  priorityCode: 'Pri',
};

/**
 * Status names the legacy CMS2 added as hidden `not` predicates to every
 * services-list query. Pre-rebuild saved reports may still carry these
 * predicates; loading them as-is fights with positive status filters and
 * triggers BFF 502s. The current build prefers explicit ticks via
 * `getOpenStatusFilterModel` instead — these names are kept only so the
 * legacy items can be recognised and stripped on load.
 */
const LEGACY_HIDDEN_STATUS_VALUES: ReadonlySet<string> = new Set(['Complete', 'Cancelled']);

/** Names of all active, non-closed statuses — i.e. the "Open" group.
 *  Deduplicates by name so statuses that share a display name (e.g. two
 *  "Examining" codes) contribute only one entry to the filter value array. */
export const getOpenStatusNames = (
  statuses: Array<{ name: string; isActive: boolean; isClosed?: boolean }> | undefined
): string[] => {
  if (!statuses) return [];
  const seen = new Set<string>();
  return statuses
    .filter((s) => {
      if (!s.isActive || s.isClosed) return false;
      if (seen.has(s.name)) return false;
      seen.add(s.name);
      return true;
    })
    .map((s) => s.name);
};

/**
 * The new "show me open jobs" default — every active non-closed status
 * ticked, surfaced as an `isAnyOf` filter so it shows up explicitly in the
 * FilterBar instead of being hidden in the request.
 *
 * Returns an empty filter model if no open statuses are known yet (e.g.
 * the statuses query hasn't resolved); callers should defer applying the
 * default in that case.
 */
export const getOpenStatusFilterModel = (
  statuses: Array<{ name: string; isActive: boolean; isClosed?: boolean }> | undefined
): { items: GridFilterItem[] } => {
  const openNames = getOpenStatusNames(statuses);
  if (openNames.length === 0) return { items: [] };
  return {
    items: [{ field: 'serviceStatusName', operator: 'isAnyOf', value: openNames }],
  };
};

/**
 * Strip the legacy hidden-default pair (`serviceStatusName not Complete`
 * + `serviceStatusName not Cancelled`) that the old CMS2 baked into
 * every saved report. In the new model the same intent is expressed by
 * ticking open statuses explicitly, so these negations are vestigial.
 *
 * Only strips when **both** predicates are present together — that's
 * the signature of the auto-injected hidden default. A solitary
 * `not Complete` or `not Cancelled` is treated as a deliberate user
 * filter and left alone.
 *
 * If after stripping the report has no `serviceStatusName` predicate at
 * all, callers can substitute the open-statuses default themselves so the
 * UI shows the same set of jobs as the legacy report did.
 */
export const stripLegacyServiceStatusDefaults = (items: GridFilterItem[]): GridFilterItem[] => {
  const seen = new Set<string>();
  for (const item of items) {
    if (
      item.field === 'serviceStatusName' &&
      item.operator === 'not' &&
      typeof item.value === 'string' &&
      LEGACY_HIDDEN_STATUS_VALUES.has(item.value)
    ) {
      seen.add(item.value);
    }
  }
  if (seen.size < LEGACY_HIDDEN_STATUS_VALUES.size) return items;

  return items.filter(
    (item) =>
      !(
        item.field === 'serviceStatusName' &&
        item.operator === 'not' &&
        typeof item.value === 'string' &&
        LEGACY_HIDDEN_STATUS_VALUES.has(item.value)
      )
  );
};

/** True if the items list contains any predicate on `serviceStatusName`. */
export const hasServiceStatusPredicate = (items: GridFilterItem[]): boolean =>
  items.some((item) => item.field === 'serviceStatusName');

/**
 * Apply the saved-report cleanup pipeline used by `applyReportConfig` (in
 * useServiceListData) so callers that derive FilterBar state from a saved
 * report see the same items the BFF query receives. Without this, the chips
 * bar shows raw legacy `not Complete` / `not Cancelled` predicates as
 * orphan chips while the underlying request silently strips them.
 */
export const cleanSavedReportFilterModel = (model: {
  items: GridFilterItem[];
}): { items: GridFilterItem[] } => ({
  items: sanitizeSavedReportFilterItems(stripLegacyServiceStatusDefaults(model.items ?? [])),
});

/** Build column-picker state with `value` aligned to the supplied visible
 *  fields so the picker's ticked set matches what the table actually renders
 *  on first load. The `value` flags in the raw `columnSelectionItems`
 *  constant are ignored — the passed-in list is the single source of truth.
 *  Defaults to the standard internal-user layout; pass
 *  CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS for the curated client view. */
export const buildInitialColumnSelection = (
  visibleFields: ReadonlyArray<string> = DEFAULT_VISIBLE_FIELDS
): ColumnSelectionItem[] =>
  columnSelectionItems.map((group) => ({
    ...group,
    value: group.value.map((col) => ({
      ...col,
      value: visibleFields.includes(col.field),
    })),
  }));

/** Map columnSelectionItems to ColumnDef[] for ServiceTable, then apply the
 *  default column order so visible columns render in the configured sequence.
 *  Defaults to the standard internal-user layout; pass
 *  CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS for the curated client view.
 *
 *  The `serviceAddressSplit` picker entry is a master toggle — one tickbox
 *  that controls seven underlying grid columns. We expand it into the
 *  per-field ColumnDefs here so the table has real columns to render and so
 *  reorder/visibility logic can address each split independently. The master
 *  toggle never becomes a grid column on its own. */
export const buildColumnDefs = (
  visibleFields: ReadonlyArray<string> = DEFAULT_VISIBLE_FIELDS
): ColumnDef[] => {
  const defs: ColumnDef[] = [];
  for (const group of columnSelectionItems) {
    for (const col of group.value) {
      if (col.field === SERVICE_ADDRESS_SPLIT_TOGGLE_FIELD) {
        for (const split of SERVICE_ADDRESS_SPLIT_COLUMNS) {
          defs.push({
            key: split.field,
            label: split.title,
            group: group.title,
            visible: visibleFields.includes(split.field),
          });
        }
        continue;
      }
      defs.push({
        key: col.field,
        label: col.title,
        group: group.title,
        visible: visibleFields.includes(col.field),
        ...(DEFAULT_COLUMN_HEADER_LABELS[col.field] !== undefined
          ? { headerLabel: DEFAULT_COLUMN_HEADER_LABELS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_WIDTHS[col.field] !== undefined
          ? { width: DEFAULT_COLUMN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MIN_WIDTHS[col.field] !== undefined
          ? { minWidth: DEFAULT_COLUMN_MIN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MAX_WIDTHS[col.field] !== undefined
          ? { maxWidth: DEFAULT_COLUMN_MAX_WIDTHS[col.field] }
          : {}),
      });
    }
  }
  return applyColumnOrder(
    defs,
    visibleFields.map((field) => ({ field }))
  );
};

/** Convert FilterState from FilterBar into a GridFilterModel for the API. */
export const buildFilterModel = (filters: FilterState): { items: GridFilterItem[] } => {
  const items: GridFilterItem[] = [];

  if (filters.jobId) {
    // Accept multiple job IDs separated by any mix of whitespace (spaces, and
    // the tabs / newlines produced by pasting Excel columns or rows) and
    // commas. Keep only numeric tokens — serviceId is cast to an integer on the
    // backend, so a stray non-numeric cell (e.g. a pasted "Job ID" header)
    // would otherwise error the whole query — then de-dupe.
    const jobIds = [
      ...new Set(
        filters.jobId
          .split(/[\s,]+/)
          .filter((t) => /^\d+$/.test(t))
          // Strip leading zeros so a pasted "001" de-dupes against "1" (serviceId
          // is an integer column; both resolve to the same row).
          .map((t) => t.replace(/^0+(?=\d)/, ''))
      ),
    ];
    if (jobIds.length) {
      items.push({ field: 'serviceId', operator: 'isAnyOf', value: jobIds });
    }
  }

  // Status: values in FilterState are status codes (fresh picks) or legacy
  // names (backward compat when no lookup map is present). Convert codes to
  // names via statusCodeToName; fall back to treating the value as a name
  // directly so old state (e.g. URL hydration without cache) still works.
  const statusCodeToName = filters.statusCodeToName ?? {};
  const namesToEmit = filters.statuses.map((code) => statusCodeToName[code] ?? code);
  const uniqueNames = [...new Set(namesToEmit)];
  if (uniqueNames.length === 1) {
    items.push({ field: 'serviceStatusName', operator: 'equals', value: uniqueNames[0] });
  } else if (uniqueNames.length > 1) {
    items.push({ field: 'serviceStatusName', operator: 'isAnyOf', value: uniqueNames });
  }

  if (filters.jobTypes.length === 1) {
    items.push({ field: 'serviceTypeName', operator: 'equals', value: filters.jobTypes[0] });
  } else if (filters.jobTypes.length > 1) {
    items.push({ field: 'serviceTypeName', operator: 'isAnyOf', value: filters.jobTypes });
  }

  // Contract: values are already the contract names the BFF matches on, so
  // they go straight out — no lookup map, unlike the client slot below.
  if (filters.contractNames.length === 1) {
    items.push({ field: 'contractName', operator: 'equals', value: filters.contractNames[0] });
  } else if (filters.contractNames.length > 1) {
    items.push({ field: 'contractName', operator: 'isAnyOf', value: filters.contractNames });
  }

  // Services repurposes the FilterState `contracts` slot for client ids
  // (fresh picks) or legacy values (names/codes from saved reports).
  // buildFilterModel converts ids to names via contractIdToName before
  // emitting the `clientName` predicate the BFF expects.
  //
  // Values loaded from legacy saved reports are tagged in two maps:
  //   contractsLegacyHierarchy — pre-rebuild `clientHierarchy` predicates
  //   contractsLegacyCode      — post-rebuild-but-pre-name-switch `clientCode` predicates
  // Both round-trip back to their original predicate shape so saved reports
  // keep returning the same rows on re-search.
  const legacyHierarchyMap = filters.contractsLegacyHierarchy ?? {};
  const legacyCodeMap = filters.contractsLegacyCode ?? {};
  const modernContracts: string[] = [];
  const legacyHierarchyValuesByOp = new Map<string, string[]>();
  const legacyCodeValuesByOp = new Map<string, string[]>();
  for (const value of filters.contracts) {
    const hierarchyOp = legacyHierarchyMap[value];
    const codeOp = legacyCodeMap[value];
    if (hierarchyOp) {
      const bucket = legacyHierarchyValuesByOp.get(hierarchyOp) ?? [];
      bucket.push(value);
      legacyHierarchyValuesByOp.set(hierarchyOp, bucket);
    } else if (codeOp) {
      const bucket = legacyCodeValuesByOp.get(codeOp) ?? [];
      bucket.push(value);
      legacyCodeValuesByOp.set(codeOp, bucket);
    } else {
      // Client id string (fresh pick) — convert to name via contractIdToName;
      // fall back to value as-is for backward-compat with name-based state.
      const contractIdToName = filters.contractIdToName ?? {};
      modernContracts.push(contractIdToName[value] ?? value);
    }
  }

  // Modern picks: filter by client name (unique per client, unlike code).
  if (modernContracts.length === 1) {
    items.push({ field: 'clientName', operator: 'equals', value: modernContracts[0] });
  } else if (modernContracts.length > 1) {
    items.push({ field: 'clientName', operator: 'isAnyOf', value: modernContracts });
  }

  // Legacy clientHierarchy round-trip.
  for (const [op, values] of legacyHierarchyValuesByOp) {
    if (op === 'isAnyOf') {
      items.push({ field: 'clientHierarchy', operator: 'isAnyOf', value: values });
    } else {
      for (const v of values) {
        items.push({ field: 'clientHierarchy', operator: op, value: v });
      }
    }
  }

  // Legacy clientCode round-trip.
  for (const [op, values] of legacyCodeValuesByOp) {
    if (op === 'isAnyOf') {
      items.push({ field: 'clientCode', operator: 'isAnyOf', value: values });
    } else {
      for (const v of values) {
        items.push({ field: 'clientCode', operator: op, value: v });
      }
    }
  }

  for (const adv of filters.advancedFilters) {
    // `between` is a UI-only operator — the backend expects a pair of bounded
    // comparisons (onOrAfter/onOrBefore for dates, >=/<= for numbers). Emit
    // both items so the API receives e.g. `closedAt=onOrAfter,…&closedAt=onOrBefore,…`.
    if (adv.operator === 'between') {
      const fromValue = adv.dateFrom ?? adv.value;
      const toValue = adv.dateTo ?? adv.value2 ?? '';
      if (adv.type === 'date') {
        if (fromValue) items.push({ field: adv.dataKey, operator: 'onOrAfter', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: 'onOrBefore', value: toValue });
      } else {
        if (fromValue) items.push({ field: adv.dataKey, operator: '>=', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: '<=', value: toValue });
      }
      continue;
    }
    // Services talks to api-services, which AND's repeated same-field params —
    // so a multi-value `Is not` expands to one `not` item per value.
    items.push(...buildAdvancedFilterItems(adv, 'repeatNot'));
  }

  return { items };
};

/** URL search params relevant to the service list deep-links. */
export interface ServiceListSearchParams {
  locationId: string | null;
  importSessionId: string | null;
  clientCode: string | null;
  serviceTypeName: string | null;
  serviceStatusGroup: string | null;
  structureNodeId: string | null;
  defaultReport: string | undefined;
}

/**
 * Build the initial filter model from URL search params.
 *
 * Priority order (first match wins):
 * 1. locationId  → empty filters (BFF handles location scoping)
 * 2. importSessionId → filter by import batch
 * 3. Activity monitor (clientCode + serviceTypeName + serviceStatusGroup)
 * 4. structureNodeId → filter by structure node
 * 5. defaultReport → empty filters (BFF handles report)
 * 6. Default → empty (the page applies an open-statuses default once the
 *    statuses query resolves; see useServiceListData)
 */
export const buildInitialFilterModel = (
  params: ServiceListSearchParams
): { items: GridFilterItem[] } => {
  const {
    locationId,
    importSessionId,
    clientCode,
    serviceTypeName,
    serviceStatusGroup,
    structureNodeId,
  } = params;

  if (locationId) {
    return { items: [] };
  }
  if (importSessionId) {
    return {
      items: [{ field: 'importSessionId', operator: 'equals', value: importSessionId }],
    };
  }
  if (clientCode && serviceTypeName && serviceStatusGroup) {
    const statusNames = getServiceStatusNamesByStatusGroup(serviceStatusGroup)
      .split(',')
      .filter(Boolean);
    return {
      items: [
        { field: 'clientCode', operator: 'equals', value: clientCode },
        { field: 'serviceTypeName', operator: 'equals', value: serviceTypeName },
        ...(statusNames.length === 1
          ? [{ field: 'serviceStatusName', operator: 'equals', value: statusNames[0] }]
          : statusNames.length > 1
            ? [{ field: 'serviceStatusName', operator: 'isAnyOf', value: statusNames }]
            : []),
      ],
    };
  }
  if (structureNodeId) {
    return {
      items: [{ field: 'structureNodeId', operator: 'equals', value: structureNodeId }],
    };
  }
  return { items: [] };
};
